/*!
 * map-tiles.js — подложка карты: один источник, запасной адрес и честное слово
 * при отказе. Общий примитив всех картовых тулов.
 *
 * ИСТОЧНИК (с 2026-09-26) — OpenFreeMap, ВЕКТОР, рисует MapLibre GL.
 * До этого была растровая CARTO; 25.09.2026 она сделала коммерческое
 * использование платным выше 1 млн плиток в месяц и запретила всем серверный
 * прокси, через который карту получали российские пользователи. Почему именно
 * OpenFreeMap и как это мерилось — config/basemap.php.
 *
 * ЧТО ДЕЛАЕТ
 * ----------
 *   1. Грузит MapLibre (СВОЯ копия /vendor/maplibre/ первой, запасной — свой
 *      второй адрес, jsdelivr — последним; см. LIB ниже) и мостик
 *      Leaflet ↔ MapLibre; стиль берёт у НАС (/api/basemap/style/…, подписи на
 *      языке страницы, все адреса внутри — в схеме tmbmap://).
 *   2. Регистрирует протокол tmbmap://: каждый запрос плитки, шрифта или
 *      спрайта идёт на первый адрес, при отказе — на второй. У ru первый —
 *      наш прокси /cdn/ofm (Beget, кэш 30 дней), у остальных — прямой хост
 *      OpenFreeMap. После трёх отказов подряд при живом втором адресе карта
 *      переезжает на него целиком (asset_fallback basemap_host).
 *   3. Нет WebGL, не доехал MapLibre, не пришёл стиль, ни одной плитки ни с
 *      одного адреса — растровый запасной путь (OpenStreetMap). Не ответил и
 *      он — строка человеку вместо чёрного прямоугольника. Молчание запрещено.
 *   4. ЯРКОСТЬ подложки следует теме сайта, кнопка на карте её перекрывает, и
 *      выбор один на все картовые тулы (ключ ts_map_basemap).
 *   5. Для ЭКСПОРТА (PNG/PDF/MP4 /gpx-viewer, картинка /true-size-map) отдаёт
 *      отрисованный кусок карты: snapshot() — вид целиком, tileImage() —
 *      плитка 256 px (рендерит блоками во внеэкранной карте MapLibre).
 *
 * API:
 *   __mapTiles.base(L, map, _, cfg) → L.LayerGroup (подложка; её можно
 *       снимать и возвращать, например из L.control.layers)
 *     cfg.slug        — для телеметрии
 *     cfg.style       — 'auto' (следует яркости) | 'dark' | 'light' | 'street'
 *     cfg.styles      — старая форма «участвую в выборе яркости» (= 'auto')
 *     cfg.credit      — чьи ДАННЫЕ на карте («Данные © USGS»); подпись самой
 *                       подложки примитив ставит сам
 *     cfg.opts        — { maxZoom, pane }: maxZoom — предел зума карты (его
 *                       держит сама подложка, по умолчанию 19), pane — для
 *                       растрового пути
 *     cfg.text / cfg.onExhausted — что сказать, если подложки нет совсем
 *     cfg.control / controlPosition / labels / onStyle — кнопка яркости
 *   __mapTiles.mode() / resolve() / setMode(m) — выбор яркости
 *   __mapTiles.snapshot({style, zoom, x, y, width, height, pixelRatio})
 *       → Promise<canvas|null>; zoom в шкале плиток 256 px, x/y — мировые
 *       пиксели левого верхнего угла на этом зуме
 *   __mapTiles.tileImage(style, z, x, y, {retina}) → Promise<canvas|null>
 *
 *   Элемент с атрибутом `data-basemap-host` получает `data-basemap=light|dark`
 *   ЗДЕСЬ ЖЕ, при разборе файла, — чтобы светлая карта не мигала тёмной
 *   панелью до первого кадра.
 */
(function (root) {
  'use strict';

  var SWITCH_AFTER = 8;   // растровых плиток подряд; экран это ~1/2 видимой сетки

  function report(event, data) {
    try { root.__ae && root.__ae(event, data || {}); } catch (e) {}
  }

  /* Часы ВИДИМОГО времени: скрытая вкладка не рисует и не грузит, и дедлайн
     по стенным часам превращал «человек отвлёкся» в «карта сломана» (пилот
     вектора 01–25.09: 11% откатов на растр по таймауту стиля). */
  function later(fn, ms) {
    try { if (typeof root.__visibleTimeout === 'function') return root.__visibleTimeout(fn, ms); } catch (e) {}
    var h = setTimeout(fn, ms);
    return function () { clearTimeout(h); };
  }

  var ML_VER = '5.6.0', BRIDGE_VER = '0.1.0', RTL_VER = '0.3.0';
  var SCHEME = 'tmbmap';
  var UPSTREAM = 'https://tiles.openfreemap.org', PROXY = '/cdn/ofm';
  var RASTER = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
  var ATTR_VECTOR = '<a href="https://openfreemap.org" target="_blank" rel="noopener">OpenFreeMap</a>' +
    ' <a href="https://www.openmaptiles.org/" target="_blank" rel="noopener">&copy; OpenMapTiles</a>' +
    ' Data from <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>';
  var ATTR_RASTER = '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>';
  var STYLES = { dark: 1, light: 1, street: 1 };

  /**
   * Сказать словами, что подложки нет. Узел создаётся ЗДЕСЬ, а не в блейде:
   * иначе к каждому туду пришлось бы добавлять свой элемент и свой стиль, и
   * половина забыла бы `[hidden]{display:none}` (класс с явным display сильнее
   * атрибута — эта ловушка уже ловила нас трижды).
   */
  function say(map, text) {
    if (!text) return;
    var host = map && map.getContainer ? map.getContainer() : null;
    if (!host || !host.parentNode) return;
    if (host.parentNode.querySelector('.mt-offline')) return;   // один раз
    var p = document.createElement('p');
    p.className = 'mt-offline';
    p.setAttribute('role', 'status');
    p.textContent = text;
    p.style.cssText = 'font-size:12px;line-height:1.5;margin:8px 0 0;color:var(--warning,#e0a33e);text-align:center';
    host.parentNode.insertBefore(p, host.nextSibling);
  }

  /* ── Яркость подложки ─────────────────────────────────────────────────
   *
   * Одно решение на все картовые тулы. Хранится под ключом __toolSettings
   * с «тулом» `map`, а не с именем конкретного тула: человек, который
   * сделал карту светлее на /earthquake-map, хотел светлую КАРТУ, а не
   * светлый earthquake-map.
   */
  var STORE_TOOL = 'map', STORE_KEY = 'basemap', LS_KEY = 'ts_map_basemap';
  var MODES = { auto: 1, light: 1, dark: 1 };
  var listeners = [];

  function readMode() {
    var v = null;
    try {
      if (root.__toolSettings && root.__toolSettings.load) {
        v = root.__toolSettings.load(STORE_TOOL, STORE_KEY, 'auto');
      } else {
        v = JSON.parse(localStorage.getItem(LS_KEY));
      }
    } catch (e) {}
    return MODES[v] ? v : 'auto';
  }

  /** Тема САЙТА — то, что человек уже выбрал в шапке или в настройках ОС. */
  function siteTheme() {
    try {
      return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
    } catch (e) { return 'dark'; }
  }

  function resolve() {
    var m = readMode();
    return m === 'auto' ? siteTheme() : m;
  }

  /**
   * Проставить `data-basemap` всем, кто попросил. Делается при разборе
   * файла и на каждую смену: тулу не нужно повторять правило выбора у себя,
   * а светлая карта не успевает мигнуть тёмной панелью до первого кадра.
   */
  function stampHosts(mode) {
    try {
      var hosts = document.querySelectorAll('[data-basemap-host]');
      for (var i = 0; i < hosts.length; i++) hosts[i].setAttribute('data-basemap', mode);
    } catch (e) {}
  }

  function announce() {
    var mode = resolve();
    stampHosts(mode);
    for (var i = 0; i < listeners.length; i++) {
      try { listeners[i](mode); } catch (e) {}
    }
  }

  function setMode(m) {
    if (!MODES[m]) return;
    try {
      if (root.__toolSettings && root.__toolSettings.save) root.__toolSettings.save(STORE_TOOL, STORE_KEY, m);
      else localStorage.setItem(LS_KEY, JSON.stringify(m));
    } catch (e) {}
    announce();
  }

  // Тема сайта поменялась (переключатель в шапке) — в режиме `auto` карта
  // обязана поехать следом, иначе «следует теме» превращается в «следовала
  // теме на момент загрузки».
  try {
    new MutationObserver(function () {
      if (readMode() === 'auto') announce();
    }).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  } catch (e) {}

  // Другая вкладка сменила выбор — здесь он тоже актуален.
  try {
    root.addEventListener('storage', function (e) {
      if (e && e.key === LS_KEY) announce();
    });
  } catch (e) {}

  var CSS_ID = 'mt-style-css';

  /* ── Токены стекла ────────────────────────────────────────────────────
   *
   * Панели, которые ПЛАВАЮТ НАД картой, обязаны следовать подложке, а не
   * теме сайта: под ними фон карты, а не фон страницы. У девяти картовых
   * тулов эта раскладка одна и та же (статус сверху, карточка справа,
   * легенда слева внизу, кнопка «моё место» справа внизу) — различаются
   * только префиксы классов. Поэтому обе палитры живут ЗДЕСЬ, а тул
   * ссылается на них: вторая копия по тулу разъехалась бы ровно так же,
   * как разъехалось правило «тёмная тема → тёмная плитка», списанное
   * семь раз подряд.
   *
   * Тул с фирменным оттенком (огонь у /wildfire-map, синь у
   * /earthquake-map) переопределяет --mt-ink* в своём блоке — токен задаёт
   * СВЕТИМОСТЬ, а не характер.
   */
  var TOKENS = '' +
    '[data-basemap]{--mt-bg:#070b14;--mt-glass:rgba(8,12,24,.6);--mt-glass-2:rgba(10,14,26,.88);' +
      '--mt-border:rgba(255,255,255,.08);--mt-border-2:rgba(255,255,255,.14);--mt-rule:rgba(255,255,255,.07);' +
      '--mt-ink:#cfe0ff;--mt-ink-dim:#8ea4c8;--mt-ink-strong:#fff;--mt-hover:rgba(255,255,255,.12);' +
      '--mt-scrim:rgba(8,12,22,.7);--mt-shadow:0 12px 40px rgba(0,0,0,.5);' +
      '--mt-attr-bg:rgba(0,0,0,.5);--mt-attr-ink:rgba(255,255,255,.55);--mt-attr-link:rgba(255,255,255,.72)}' +
    '[data-basemap=light]{--mt-bg:#e9eef2;--mt-glass:rgba(255,255,255,.84);--mt-glass-2:rgba(255,255,255,.95);' +
      '--mt-border:rgba(0,0,0,.1);--mt-border-2:rgba(0,0,0,.14);--mt-rule:rgba(0,0,0,.09);' +
      '--mt-ink:#33415c;--mt-ink-dim:#5a6b86;--mt-ink-strong:#0d1526;--mt-hover:rgba(0,0,0,.06);' +
      '--mt-scrim:rgba(240,244,248,.78);--mt-shadow:0 12px 40px rgba(15,25,45,.16);' +
      '--mt-attr-bg:rgba(255,255,255,.72);--mt-attr-ink:rgba(20,30,48,.62);--mt-attr-link:rgba(20,30,48,.85)}' +
    /* Кнопки зума Leaflet рисуются его собственным CSS — их тоже ведёт
       подложка, иначе на светлой карте остаётся чёрная пара +/−. */
    '[data-basemap] .leaflet-control-zoom a{background:var(--mt-zoom-bg,#1b2130);color:var(--mt-zoom-ink,#dbe6ff);border-color:var(--mt-border-2)}' +
    '[data-basemap=light]{--mt-zoom-bg:#fff;--mt-zoom-ink:#26303f}' +
    '[data-basemap] .leaflet-control-attribution{background:var(--mt-attr-bg);color:var(--mt-attr-ink)}' +
    '[data-basemap] .leaflet-control-attribution a{color:var(--mt-attr-link)}';

  var CSS = TOKENS +
    '.mt-style{border:none!important;box-shadow:none!important;background:none!important}' +
    '.mt-style .mt-btn{display:flex;align-items:center;justify-content:center;width:34px;height:34px;padding:0;border-radius:7px;cursor:pointer;font:inherit;' +
      'border:1px solid rgba(255,255,255,.16);background:#1b2130;color:#dbe6ff;box-shadow:0 2px 10px rgba(0,0,0,.35)}' +
    // fill:none обязателен ОБЕИМ иконкам — и в кнопке, и в меню: без него
    // луна и солнце приезжают чёрными кляксами (поймано скриншотом 23.08).
    '.mt-style svg{width:17px;height:17px;flex:none;fill:none;stroke:currentColor;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}' +
    '.mt-style[data-basemap=light] .mt-btn{background:#fff;color:#26303f;border-color:rgba(0,0,0,.16);box-shadow:0 2px 10px rgba(0,0,0,.14)}' +
    // Сторона ФИЗИЧЕСКАЯ, а не логическая: кнопка стоит в углу карты, а не в
    // потоке текста. `inset-inline-start` в арабской локали уводил меню за
    // левый край экрана (x=-121, поймано прогоном 23.08); куда открыть на
    // самом деле — решает place() по месту в контейнере.
    '.mt-style .mt-menu{position:absolute;top:0;left:40px;min-width:150px;padding:5px;border-radius:9px;' +
      'border:1px solid rgba(255,255,255,.14);background:#161c29;box-shadow:0 12px 34px rgba(0,0,0,.45)}' +
    '.mt-style[data-basemap=light] .mt-menu{background:#fff;border-color:rgba(0,0,0,.14);box-shadow:0 12px 34px rgba(0,0,0,.16)}' +
    '.mt-style .mt-menu[hidden]{display:none}' +
    '.mt-style .mt-opt{display:flex;align-items:center;gap:8px;width:100%;padding:8px 10px;border:none;border-radius:6px;background:none;cursor:pointer;' +
      'font:600 13px/1.2 inherit;color:#c8d6f0;text-align:start;white-space:nowrap}' +
    '.mt-style[data-basemap=light] .mt-opt{color:#33415c}' +
    '.mt-style .mt-opt:hover{background:rgba(255,255,255,.09)}' +
    '.mt-style[data-basemap=light] .mt-opt:hover{background:rgba(0,0,0,.05)}' +
    '.mt-style .mt-opt[aria-checked=true]{color:#fff;background:rgba(90,134,255,.28)}' +
    '.mt-style[data-basemap=light] .mt-opt[aria-checked=true]{color:#12203f;background:rgba(43,78,199,.14)}' +
    '.mt-style .mt-tick{width:14px;flex:none;opacity:0}' +
    '.mt-style .mt-opt[aria-checked=true] .mt-tick{opacity:1}';

  function injectCss() {
    if (document.getElementById(CSS_ID)) return;
    var st = document.createElement('style');
    st.id = CSS_ID;
    st.textContent = CSS;
    (document.head || document.documentElement).appendChild(st);
  }

  var ICONS = {
    auto: '<rect x="2" y="4" width="20" height="13" rx="2"/><path d="M8 21h8M12 17v4"/>',
    light: '<circle cx="12" cy="12" r="4.6"/><path d="M12 1.6v2.2M12 20.2v2.2M4.2 4.2l1.6 1.6M18.2 18.2l1.6 1.6M1.6 12h2.2M20.2 12h2.2M4.2 19.8l1.6-1.6M18.2 5.8l1.6-1.6"/>',
    dark: '<path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8z"/>'
  };
  function icon(name) {
    return '<svg viewBox="0 0 24 24" aria-hidden="true">' + ICONS[name] + '</svg>';
  }

  /**
   * Кнопка выбора яркости. Три состояния, а не два: «как на сайте» — это
   * отдельный ответ, и без него человек, однажды нажавший «светлая», навсегда
   * терял связь с переключателем темы в шапке.
   */
  function styleControl(L, map, cfg) {
    if (!L || !L.Control || !map) return null;
    injectCss();
    var lbl = cfg.labels || {};
    var texts = {
      auto: lbl.auto || 'Same as site',
      light: lbl.light || 'Light',
      dark: lbl.dark || 'Dark'
    };
    var slug = cfg.slug || '';

    var Ctl = L.Control.extend({
      options: { position: cfg.controlPosition || 'topleft' },
      onAdd: function () {
        var wrap = L.DomUtil.create('div', 'leaflet-control mt-style');
        wrap.style.position = 'relative';

        var btn = L.DomUtil.create('button', 'mt-btn', wrap);
        btn.type = 'button';
        btn.setAttribute('aria-haspopup', 'true');
        btn.setAttribute('aria-expanded', 'false');
        btn.title = lbl.title || 'Map brightness';
        btn.setAttribute('aria-label', btn.title);

        var menu = L.DomUtil.create('div', 'mt-menu', wrap);
        menu.setAttribute('role', 'menu');
        menu.hidden = true;

        function close() { menu.hidden = true; btn.setAttribute('aria-expanded', 'false'); }
        /* Открыть внутрь карты. Кнопка может стоять в любом её углу, а сама
           карта — в RTL-странице, поэтому сторона выбирается по замеру, а не
           по направлению текста. */
        function place() {
          menu.style.left = '40px'; menu.style.right = 'auto';
          try {
            var host = map.getContainer().getBoundingClientRect();
            var w = menu.getBoundingClientRect(), b = btn.getBoundingClientRect();
            if (w.right > host.right - 6 || b.left + 40 + w.width > host.right - 6) {
              menu.style.left = 'auto'; menu.style.right = '40px';
            }
          } catch (e) {}
        }
        function open() { menu.hidden = false; place(); btn.setAttribute('aria-expanded', 'true'); }

        var opts = {};
        ['auto', 'light', 'dark'].forEach(function (m) {
          var o = L.DomUtil.create('button', 'mt-opt', menu);
          o.type = 'button';
          o.setAttribute('role', 'menuitemradio');
          o.innerHTML = '<span class="mt-tick" aria-hidden="true">✓</span>' + icon(m) + '<span></span>';
          o.lastChild.textContent = texts[m];
          o.addEventListener('click', function () {
            setMode(m);
            close();
            report('feature_use', { slug: slug, feature: 'basemap', mode: m });
          });
          opts[m] = o;
        });

        function paint() {
          var chosen = readMode(), shown = resolve();
          wrap.setAttribute('data-basemap', shown);
          btn.innerHTML = icon(chosen);
          for (var m in opts) opts[m].setAttribute('aria-checked', m === chosen ? 'true' : 'false');
        }

        btn.addEventListener('click', function () { menu.hidden ? open() : close(); });
        wrap.addEventListener('keydown', function (e) { if (e.key === 'Escape') { close(); btn.focus(); } });
        document.addEventListener('click', function (e) { if (!wrap.contains(e.target)) close(); });

        listeners.push(paint);
        paint();

        // Иначе клик по кнопке доезжает до карты и двигает вид / ставит точку.
        L.DomEvent.disableClickPropagation(wrap);
        L.DomEvent.disableScrollPropagation(wrap);
        return wrap;
      }
    });
    var c = new Ctl();
    c.addTo(map);
    return c;
  }

  /* Растровый запасной путь — OpenStreetMap, у него есть только светлая
     плитка. Тёмную получаем фильтром: негатив + разворот оттенка возвращает
     воду синеватой, а текст светлым. Это откат на доли процента посетителей,
     а не основная подложка. */
  CSS += '.mt-osm-dark{filter:invert(1) hue-rotate(180deg) brightness(.9) contrast(.9) saturate(.55)}' +
    '.leaflet-gl-layer{pointer-events:none}';

  /* ── Движок: MapLibre + мостик Leaflet ────────────────────────────────── */

  /* WebGL1 хватает: MapLibre 5.6 сам откатывается с webgl2 на webgl, и на
     Chromium без webgl2 векторная карта вставала (замер 15.09.2026). ⚠️ Это
     ПРЕДпроверка, а не обещание: iPad на iOS 12 её проходил, а конструктор
     карты всё равно бросал (Sentry KB/KC/KD) — поэтому ниже есть и перехват
     исключения из onAdd, и память отказа устройства. */
  var glOk = null, glWhy = 'no_webgl';
  function webglOk() {
    if (glOk !== null) return glOk;
    if (!blobUrlOk()) { glWhy = 'no_blob_url'; return (glOk = false); }
    try {
      var c = document.createElement('canvas');
      glOk = !!(c.getContext('webgl2') || c.getContext('webgl'));
    } catch (e) { glOk = false; }
    return glOk;
  }

  /* MapLibre собирает свой воркер из Blob-адреса уже при исполнении файла:
     `URL.createObjectURL(new Blob([…]))` в его define, до всякой карты.
     Расширение «приватности», подменившее window.Blob, валит этот шаг — «Blob
     is not a constructor» прямо из движка, и 240 КБ скачаны зря (Sentry
     TIMBRICA-FRONTEND-NW, Chrome 116 на Linux; тот же отпечаток описан у
     полифилла arrayBuffer в timbrica-core.js). Проверяется ТЕМ ЖЕ действием:
     не выходит оно — не грузим и движок, подложка сразу растровая. */
  function blobUrlOk() {
    try {
      URL.revokeObjectURL(URL.createObjectURL(new Blob([''], { type: 'text/javascript' })));
      return true;
    } catch (e) { return false; }
  }

  /* Устройство, на котором конструктор карты уже бросал, неделю не качает
     MapLibre зря: ответ тот же, а плата — 240 КБ и секунды ожидания. */
  var VEC_FAIL_KEY = 'ts_map_vector_failed';
  function vectorRefused() {
    try {
      var t = +localStorage.getItem(VEC_FAIL_KEY);
      return !!t && Date.now() - t < 7 * 864e5;
    } catch (e) { return false; }
  }
  function refuseVector() {
    try { localStorage.setItem(VEC_FAIL_KEY, String(Date.now())); } catch (e) {}
  }

  function absolute(u) {
    try { return new URL(u, location.href).href; } catch (e) { return u; }
  }

  /* ── Откуда брать MapLibre: своя копия первой (16.10.2026) ──────────────
     До этой даты движок, его стиль, мостик и модуль вязи шли с jsdelivr:
     intl — напрямую, ru — через прокси Бегета /cdn/jsdelivr, который при
     холодном кэше сам ходит на jsdelivr. Просьба Фарида — каждая половина
     сайта отдаёт свои файлы сама. Копии лежат в public/vendor/maplibre/ той
     же версии, побайтно равные npm (лицензии рядом); версия — в ПУТИ, поэтому
     вечный кэш /vendor/ им не страшен.
     Порядок — правило «своя копия первой» (vendor-own-copy.md, как у
     cdn_chain()): свой адрес → свой запасной (у ru — второй origin
     assets.timbrica.com, у intl и на стенде — тот же файл с ?fb=1; шаблон
     кладёт layout в window.__LIGHT_FB из AssetRouter::lightFallback) → прежние
     адреса jsdelivr (прокси Бегета и прямой) — последняя соломинка. Выручил
     не первый — asset_fallback {asset, index}. У стиля раньше запасного не
     было вовсе: при отказе прокси ru рисовал карту без maplibre-gl.css. */
  var OWN = '/vendor/maplibre/';
  var LIB = {
    core: { own: OWN + 'maplibre-gl-' + ML_VER + '/maplibre-gl.js', npm: '/npm/maplibre-gl@' + ML_VER + '/dist/maplibre-gl.js' },
    css: { own: OWN + 'maplibre-gl-' + ML_VER + '/maplibre-gl.css', npm: '/npm/maplibre-gl@' + ML_VER + '/dist/maplibre-gl.css' },
    bridge: { own: OWN + 'maplibre-gl-leaflet-' + BRIDGE_VER + '/leaflet-maplibre-gl.js', npm: '/npm/@maplibre/maplibre-gl-leaflet@' + BRIDGE_VER + '/leaflet-maplibre-gl.js' },
    rtl: { own: OWN + 'mapbox-gl-rtl-text-' + RTL_VER + '/mapbox-gl-rtl-text.js', npm: '/npm/@mapbox/mapbox-gl-rtl-text@' + RTL_VER + '/dist/mapbox-gl-rtl-text.js' }
  };
  function ownFallback(path) {
    var t = root.__LIGHT_FB;
    if (typeof t === 'string' && t.indexOf('{p}') >= 0) return t.replace('{p}', path.replace(/^\//, ''));
    return path + (path.indexOf('?') < 0 ? '?' : '&') + 'fb=1';
  }
  function chainOf(lib) {
    var list = [lib.own, ownFallback(lib.own)];
    try {
      var c = root.__CDN;
      if (c && c.url) list.push(c.url('jsdelivr', lib.npm));
      if (c && c.altUrl) list.push(c.altUrl('jsdelivr', lib.npm));
    } catch (e) {}
    list.push('https://cdn.jsdelivr.net' + lib.npm);
    var seen = {}, out = [];
    for (var i = 0; i < list.length; i++) {
      if (list[i] && !seen[list[i]]) { seen[list[i]] = 1; out.push(list[i]); }
    }
    return out;
  }
  /* Номер адреса, с которого пришёл движок: модуль вязи MapLibre грузит в
     своём воркере ОДНИМ адресом (setRTLTextPlugin второй раз не позвать),
     и надёжнее всего тот хост, что уже ответил. */
  var coreFrom = 0;
  function fellBack(name, i, url) {
    if (i > 0) report('asset_fallback', { slug: pageSlug(), asset: name, index: i, url: String(url).slice(0, 160) });
  }

  /* Попытка, не ответившая ни onload, ни onerror, не держит цепочку вечно:
     30 с ВИДИМОГО времени — и следующий адрес. Срок ловит тишину, а не
     медленную сеть: сама подложка уходит на растр через 15 с, этот срок —
     для экспорта (snapshot) и следующей карты на странице. */
  var ATTEMPT_MS = 30000;
  function loadScript(lib, name, ok) {
    var urls = chainOf(lib);
    return new Promise(function (res, rej) {
      var i = 0;
      function next(err) {
        if (i >= urls.length) { rej(err || new Error('load ' + name)); return; }
        var k = i++, url = urls[k], done = false, stop = null;
        var s = document.createElement('script');
        function fail(e) { if (done) return; done = true; if (stop) stop(); next(e); }
        s.src = url;
        s.async = true;
        s.onload = function () {
          if (done) return;
          /* 200 без глобала (страница-заглушка вместо скрипта) — тот же
             отказ адреса. */
          if (ok && !ok()) { fail(new Error(name + '-missing')); return; }
          done = true;
          if (stop) stop();
          fellBack(name, k, url);
          res(k);
        };
        s.onerror = function () { fail(new Error('load ' + name)); };
        stop = later(function () { fail(new Error('stalled ' + name)); }, ATTEMPT_MS);
        document.head.appendChild(s);
      }
      next(null);
    });
  }
  function loadCss(lib) {
    var id = 'mt-css-maplibre';
    if (document.getElementById(id)) return;
    var urls = chainOf(lib), i = 0, said = false;
    var l = document.createElement('link');
    l.id = id;
    l.rel = 'stylesheet';
    l.onerror = function () { if (i < urls.length - 1) l.href = urls[++i]; };
    /* WebKit зовёт onload сменённого href дважды — одно событие на загрузку. */
    l.onload = function () { if (said) return; said = true; fellBack('maplibre_css', i, urls[i]); };
    l.href = urls[0];
    (document.head || document.documentElement).appendChild(l);
  }

  var coreLoad = null, bridgeLoad = null;
  /** MapLibre без мостика — для внеэкранного рендера (экспорт). */
  function ensureCore() {
    if (root.maplibregl) { setupMapLibre(root.maplibregl); return Promise.resolve(root.maplibregl); }
    if (coreLoad) return coreLoad;
    loadCss(LIB.css);
    coreLoad = loadScript(LIB.core, 'maplibre', function () { return !!root.maplibregl; }).then(function (k) {
      coreFrom = k;
      setupMapLibre(root.maplibregl);
      return root.maplibregl;
    });
    return coreLoad;
  }
  /**
   * MapLibre + мостик в Leaflet. ⚠️ ДВЕ РАЗНЫЕ ЗАГРУЗКИ: `L.maplibreGL` — это
   * мостик, а рисует `window.maplibregl`. Мостик при исполнении захватывает
   * ОБА глобала, поэтому грузится строго после движка и после Leaflet; слой,
   * смонтированный с мостиком без движка, выглядит живым и падает на первом
   * зуме («Cannot read properties of undefined (reading 'jumpTo')»).
   */
  function ensureBridge(L) {
    if (L && L.maplibreGL && root.maplibregl) { setupMapLibre(root.maplibregl); return Promise.resolve(root.maplibregl); }
    if (bridgeLoad) return bridgeLoad;
    bridgeLoad = ensureCore().then(function (ml) {
      if (L && L.maplibreGL) return ml;
      if (!root.L) throw new Error('leaflet-missing');
      return loadScript(LIB.bridge, 'maplibre_bridge', function () { return !!(L || root.L).maplibreGL; }).then(function () {
        return ml;
      });
    });
    return bridgeLoad;
  }

  /* ── Протокол tmbmap:// ───────────────────────────────────────────────── */

  var proto = { bases: null, active: 0, fails: [0, 0], tileOk: 0, switched: false, onTile: [] };

  function protoBases() {
    if (proto.bases) return proto.bases;
    var m = root.__CDN && root.__CDN.map, ofm = m && m.ofm;
    var proxy = absolute((ofm && ofm.vps) || PROXY), direct = (ofm && ofm.origin) || UPSTREAM;
    proto.bases = root.__IS_RU ? [proxy, direct] : [direct, proxy];
    return proto.bases;
  }

  function isTile(path) { return /\.(pbf|mvt)(\?|$)/.test(path) && path.indexOf('fonts/') !== 0; }
  function hostOf(u) { try { var x = new URL(u); return x.host + x.pathname.replace(/\/$/, ''); } catch (e) { return String(u); } }
  function pageSlug() { return String(root.__TOOL_SLUG || ''); }

  function noteOk(i, path) {
    proto.fails[i] = 0;
    if (isTile(path)) {
      proto.tileOk++;
      var cbs = proto.onTile.slice();
      proto.onTile.length = 0;
      for (var k = 0; k < cbs.length; k++) { try { cbs[k](); } catch (e) {} }
    }
  }
  function noteFail(i, err) {
    proto.fails[i]++;
    /* Первый адрес отказал трижды подряд, второй при этом не отказывал —
       переезжаем целиком, а не платим лишним кругом за каждый запрос. */
    if (i === proto.active && proto.fails[i] >= 3 && proto.fails[1 - i] === 0) {
      proto.active = 1 - i;
      if (!proto.switched) {
        proto.switched = true;
        var b = protoBases();
        report('asset_fallback', { slug: pageSlug(), asset: 'basemap_host', from: hostOf(b[i]), to: hostOf(b[1 - i]), err: String((err && err.message) || err).slice(0, 80) });
      }
    }
  }

  function protoFetch(params, ac) {
    var bases = protoBases();
    var path = String(params.url || '').slice(SCHEME.length + 3);
    var order = [proto.active, 1 - proto.active];
    var signal = ac && ac.signal;
    function attempt(k) {
      var i = order[k];
      return fetch(bases[i] + '/' + path, { signal: signal, credentials: 'omit' }).then(function (r) {
        /* 204 — ответ, а не отказ: плитки нет, пустой вектор MapLibre
           рисует пустой плиткой. А 404 — ОТКАЗ адреса: OpenFreeMap отвечает
           200 даже на пустой океан (0 байт) и на устаревшую версию данных,
           так что 404 значит одно — у нашего прокси нет локации (страница
           ошибки Laravel, text/html). Прочитать его как «пусто» значило бы
           показать ПУСТУЮ карту без отката — ровно то, что ловили у пилота
           01.09 (ru получал 0 плиток). */
        if (r.status === 204) {
          noteOk(i, '');
          return { data: new ArrayBuffer(0) };
        }
        if (!r.ok) throw new Error('HTTP ' + r.status);
        // Страница вместо данных (заглушка провайдера, капча, ошибка прокси
        // с кодом 200) — тоже отказ адреса, а не плитка.
        if (/text\/html/i.test(r.headers.get('Content-Type') || '')) throw new Error('html');
        return (params.type === 'json' ? r.json() : r.arrayBuffer()).then(function (d) {
          noteOk(i, path);
          return { data: d, cacheControl: r.headers.get('Cache-Control'), expires: r.headers.get('Expires') };
        });
      }).catch(function (e) {
        if (signal && signal.aborted) throw e;
        noteFail(i, e);
        if (k + 1 < order.length) return attempt(k + 1);
        throw e;
      });
    }
    return attempt(0);
  }

  var mlReady = false;
  function setupMapLibre(ml) {
    if (mlReady || !ml) return;
    mlReady = true;
    try { ml.addProtocol(SCHEME, protoFetch); } catch (e) {}
    /* Арабская вязь и иврит без этого модуля рисуются отдельными буквами
       слева направо. Грузится ЛЕНИВО — только когда на карте встретился
       такой текст (у ar это сразу, у остальных — редкие места без своего
       имени). Отказ модуля не ломает карту. */
    try {
      if (!ml.getRTLTextPluginStatus || ml.getRTLTextPluginStatus() === 'unavailable') {
        var rtl = chainOf(LIB.rtl);
        var p = ml.setRTLTextPlugin(absolute(rtl[Math.min(coreFrom, rtl.length - 1)]), true);
        if (p && p.catch) p.catch(function () {});
      }
    } catch (e) {}
  }

  /* Сам MapLibre (240 КБ gzip) начинает грузиться сразу, при разборе этого
     файла: он подключается ТОЛЬКО на картовых страницах, и ждать, пока тул
     дойдёт до карты, значит отдать секунду на ровном месте. Мостик — позже,
     ему нужен Leaflet. */
  if (webglOk() && !vectorRefused()) {
    try { ensureCore().catch(function () {}); } catch (e) {}
  }

  function styleUrl(key) {
    var loc = root.__LOCALE || document.documentElement.getAttribute('lang') || 'en';
    return absolute('/api/basemap/style/' + (STYLES[key] ? key : 'dark') + '.json?loc=' + encodeURIComponent(loc));
  }

  /* ── Подложка на карте ────────────────────────────────────────────────── */

  /** Какую подложку просит тул. Понимает и старые формы конфига. */
  function styleKeyOf(cfg) {
    var s = cfg.style;
    if (s === 'auto' || STYLES[s]) return s;
    if (cfg.styles) return 'auto';
    var p = String(cfg.path || '');
    if (p.indexOf('light') >= 0) return 'light';
    if (p.indexOf('voyager') >= 0) return 'street';
    return 'dark';
  }

  /**
   * Подпись ДАННЫХ тула. Подпись подложки ставит примитив, а старые строки
   * тулов («Данные © USGS · Карта © OpenStreetMap, CARTO») несут ещё и
   * прежнюю подложку — такие части выкидываются, чтобы не подписывать чужую
   * карту и не дублировать OpenStreetMap.
   */
  function creditOf(cfg) {
    var raw = typeof cfg.credit === 'string' ? cfg.credit : ((cfg.opts && cfg.opts.attribution) || '');
    return String(raw).split(/\s+·\s+/).filter(function (seg) {
      return seg && !/CARTO|OpenStreetMap|\bOSM\b|OpenFreeMap/i.test(seg);
    }).join(' · ');
  }

  // Щипок Leaflet 1.9.4 делит текущее расстояние между пальцами на НАЧАЛЬНОЕ
  // без проверки: два касания, сообщённые в одной точке, дали бы масштаб
  // Infinity/NaN. Щипок отсчитывается от того места, где пальцы РАЗОШЛИСЬ;
  // пока они в одной точке, масштабировать не по чему.
  // ⚠️ Писалось ради Sentry TIMBRICA-FRONTEND-N1/N2 (/map-measure, «Invalid
  // LatLng object: (NaN, NaN)»), но их не лечило: ни одного стека из
  // _onTouchMove там нет, а N1 падала уже в _onTouchStart — карта была
  // сломана ДО щипка. Их корень — зум Infinity, см. zoomCeiling() ниже.
  function steadyPinch(L) {
    var Z = L && L.Map && L.Map.TouchZoom && L.Map.TouchZoom.prototype;
    if (!Z || Z.__tbSteadyPinch || typeof Z._onTouchMove !== 'function') return;
    var move = Z._onTouchMove;
    Z._onTouchMove = function (e) {
      if (e && e.touches && e.touches.length === 2 && !(this._startDist >= 1)) {
        var m = this._map;
        var d = m.mouseEventToContainerPoint(e.touches[0]).distanceTo(m.mouseEventToContainerPoint(e.touches[1]));
        if (!(d >= 1)) return;
        this._startDist = d;
      }
      return move.call(this, e);
    };
    Z.__tbSteadyPinch = 1;
  }

  /* ── Карта не уходит туда, где подложке нечего показать ───────────────────
   *
   * Замер 07.10.2026 (/lightning-map, мировой масштаб): Leaflet даёт утащить
   * карту за полюс и отдалить её мельче, чем мир помещается в окно, а
   * MapLibre — нет: его камера не показывает ничего севернее и южнее 85,05°, а
   * мостик к тому же рисует холст на 10 % шире окна с каждой стороны, так что
   * предел наступает ещё раньше (окно 618 px, z2: уже на 44° широты). Дальше
   * Leaflet и подложка живут порознь: после протяжки на 220 px слой тула
   * (молнии, маркеры, линии) расходился с подложкой на 137 px, после 440 — на
   * 357 px, а каждые 32 мс подложку отбрасывало обратно — «всё дёргается».
   *
   * Три части, и все нужны:
   *   keepInWorld  — у карты появляются границы по широте (долгота свободна)
   *                  и нижний предел зума, при котором мир не ниже окна;
   *   alignClamped — мостик выравнивает подложку по Leaflet там, где камера
   *                  MapLibre всё же упёрлась (у самого полюса, из-за запаса
   *                  холста): картинка сдвигается на ту же разницу;
   *   тул может отказаться (`cfg.freePoles`), если ведёт полюса сам. */
  var WORLD_LAT = 85.0511287798;       // край мира в проекции Меркатора
  var GL_PAD = 0.1;                    // запас холста мостика (options.padding)

  /** Наименьший зум Leaflet, при котором мир не ниже холста подложки. */
  function minZoomFor(heightPx, snap) {
    var need = Math.log(Math.max(1, heightPx) * (1 + 2 * GL_PAD) / 256) / Math.LN2;
    var step = snap > 0 ? snap : 1;
    return Math.max(0, Math.ceil(need / step - 1e-9) * step);
  }

  function keepInWorld(L, map, cfg) {
    if (cfg.freePoles || map.__tbWorld) return;
    map.__tbWorld = 1;
    try {
      if (!map.options.maxBounds) {
        map.options.maxBoundsViscosity = 1;
        map.setMaxBounds(L.latLngBounds([[-WORLD_LAT, -Infinity], [WORLD_LAT, Infinity]]));
      }
      var own = map.options.minZoom || 0;
      var fit = function () {
        var want = Math.max(own, minZoomFor(map.getSize().y, map.options.zoomSnap));
        if (want !== map.getMinZoom()) map.setMinZoom(want);
      };
      map.on('resize', fit);
      // У карты, созданной без вида, размера ещё нет — тогда по первому 'load'.
      if (map._loaded) fit(); else map.once('load', fit);
    } catch (e) {}
  }

  /** На сколько пикселей картинка MapLibre ниже того, что просил Leaflet. */
  function clampShiftPx(map, glCenterLat) {
    var want = map.getCenter(), z = map.getZoom();
    return map.project([glCenterLat, want.lng], z).y - map.project(want, z).y;
  }

  function alignClamped(L) {
    var P = L && L.MaplibreGL && L.MaplibreGL.prototype;
    if (!P || P.__tbAlign || typeof P._update !== 'function') return;
    var update = P._update;
    // Правится прототип, а не слой: мостик берёт `_update` в обёртку-дроссель
    // при создании слоя, и подмена на готовом слое до неё не доходит.
    P._update = function (e) {
      update.call(this, e);
      try {
        if (!this._map || this._zooming || !this._glMap || !this._container) return;
        var dy = clampShiftPx(this._map, this._glMap.getCenter().lat);
        if (Math.abs(dy) < 0.5) return;
        var pos = L.DomUtil.getPosition(this._container);
        L.DomUtil.setPosition(this._container, L.point(pos.x, Math.round(pos.y + dy)));
      } catch (err) {}
    };
    P.__tbAlign = 1;
  }

  /* ── Предел зума держит подложка ─────────────────────────────────────────
   *
   * Leaflet узнаёт предел зума только от слоёв, которые сами позвали
   * map._addZoomLimit в beforeAdd, — это GridLayer, то есть растровые плитки.
   * Слой-группа и мостик MapLibre — простые L.Layer: с векторной подложкой
   * (26.09.2026) у карты без своего maxZoom предела не стало вовсе,
   * getMaxZoom() — Infinity, а maxZoom в опциях мостика ограничивает только
   * камеру MapLibre. fitBounds по рамке нулевого размера (одна точка,
   * восстановленная /map-measure; две фотографии с одного места) ставил зум
   * Infinity, мир становился бесконечным, и следующий getCenter(), не
   * взятый из кэша, отдавал NaN: «Invalid LatLng object: (NaN, NaN)» из
   * мостика на первом же resize, протяжке или касании (Sentry
   * TIMBRICA-FRONTEND-N1/N2). Заодно зум уходил глубже, чем умеет камера
   * MapLibre, и слой тула расходился с подложкой.
   *
   * Предел ставится СРАЗУ, вместе с группой: мостик монтируется позже и
   * асинхронно, а тул зовёт fitBounds тут же. Снимается вместе с подложкой —
   * тул, переключивший её на спутник, получает предел своего слоя. */
  function zoomCeiling(holder, maxZoom) {
    (holder.options || (holder.options = {})).maxZoom = maxZoom;
    holder.beforeAdd = function (m) { if (m._addZoomLimit) m._addZoomLimit(this); };
    var off = holder.onRemove;
    holder.onRemove = function (m) {
      if (m._removeZoomLimit) m._removeZoomLimit(this);
      return off.call(this, m);
    };
  }

  function base(L, map, _legacyChain, cfg) {
    cfg = cfg || {};
    if (!L || !map || !L.layerGroup) return null;
    steadyPinch(L);
    keepInWorld(L, map, cfg);

    var slug = cfg.slug || pageSlug();
    var key0 = styleKeyOf(cfg);
    var follows = key0 === 'auto';
    var credit = creditOf(cfg);
    var opts = cfg.opts || {};
    var holder = L.layerGroup();
    zoomCeiling(holder, +opts.maxZoom || 19);   // тот же предел, что у растра ниже
    var st = { vec: null, ras: null, gaveUp: false, t0: Date.now() };

    function key() { return follows ? resolve() : key0; }
    function attr(a) { return a + (credit ? ' · ' + credit : ''); }

    holder.addTo(map);

    function exhausted() {
      report('tool_diagnostic', { slug: slug, issue: 'map_tiles_unreachable' });
      if (typeof cfg.onExhausted === 'function') {
        try { cfg.onExhausted(); } catch (e) {}
      } else {
        // Молчание запрещено: без строки человек видит пустой прямоугольник и
        // решает, что сломан весь инструмент.
        say(map, cfg.text);
      }
    }

    function paintRaster(r) {
      var c = r && r.getContainer && r.getContainer();
      if (c && c.classList) c.classList.toggle('mt-osm-dark', key() === 'dark');
    }

    function toRaster(why, detail) {
      if (st.ras || st.gaveUp) return;
      report('asset_fallback', { slug: slug, asset: 'basemap_vector', reason: why, detail: detail || '', ms: Date.now() - st.t0 });
      // У OpenStreetMap плитки до z19; глубже — растянутые z19, как у любой
      // растровой подложки.
      var o = { maxNativeZoom: 19, maxZoom: +opts.maxZoom || 19, attribution: attr(ATTR_RASTER) };
      if (opts.pane) o.pane = opts.pane;
      var r = L.tileLayer(root.__BASEMAP_RASTER || RASTER, o);
      var fails = 0, loaded = 0;
      r.on('tileerror', function () {
        fails++;
        if (fails === SWITCH_AFTER && !loaded) { st.gaveUp = true; exhausted(); }
      });
      r.on('tileload', function () { loaded++; fails = 0; });
      r.on('add', function () { paintRaster(r); });
      st.ras = r;
      holder.addLayer(r);
      paintRaster(r);
    }

    function mountVector() {
      if (st.ras) return;
      var l;
      try {
        /* maxZoom здесь — предел камеры MapLibre (она стоит на зуме Leaflet
           минус один), а не Leaflet: мостик — простой L.Layer, и пределов зума
           Leaflet у него не спрашивает. Предел Leaflet держит подложка —
           zoomCeiling() в base(). Вектор данных OpenFreeMap до z14, глубже
           MapLibre дорисовывает их без потери резкости. */
        alignClamped(L);
        l = L.maplibreGL({ style: styleUrl(key()), attribution: attr(ATTR_VECTOR), refreshExpiredTiles: false, maxZoom: +opts.maxZoom || 19, padding: GL_PAD });
      } catch (e) {
        toRaster('vector_ctor', String((e && e.message) || e).slice(0, 80));
        return;
      }
      var styled = false, settled = false;
      /* settled — вердикт ПЕРВОЙ загрузки: после него ни тайм-аут, ни ошибка
         стиля подложку не снимают. Отказ конструктора — другое: слой без
         карты MapLibre снимается всегда (`always`), даже если раньше он уже
         рисовал. */
      function give(why, detail, always) {
        if (settled && !always) return;
        settled = true;
        try { holder.removeLayer(l); } catch (e) {}
        if (st.vec === l) st.vec = null;
        toRaster(why, detail);
      }
      /* Leaflet вешает обработчики слоя ДО onAdd (Layer._layerAdd), поэтому
         слой, чей onAdd бросил, остаётся ПОЛУдобавленным: карта держит его, и
         каждый жест бросает из мостика (iPad iOS 12: одна ошибка конструктора,
         потом пять TypeError за 40 с). Ловим прямо в onAdd — он может
         исполниться и позже, когда карта получит вид, — и снимаем слой так,
         как снял бы Leaflet.
         onAdd исполняется и при КАЖДОМ возврате подложки: переключатель
         «Спутник» → «Карта» строит карту MapLibre заново, и конструктор может
         бросить уже здесь, после удачной первой загрузки. Пока снятие ждало
         «первого вердикта», такой слой оставался на карте с заглушкой вместо
         карты: подложки нет, а каждый кадр щипка бросал из мостика —
         «this._glMap.jumpTo is not a function» и «reading 'style'» (Sentry
         TIMBRICA-FRONTEND-KD/NR, /map-measure, Huawei с Яндекс Браузером,
         03.10.2026). Теперь он снимается и тут, и на его место встаёт растр. */
      var onAdd = l.onAdd;
      l.onAdd = function (m) {
        try { return onAdd.call(this, m); } catch (e) {
          var self = this;
          refuseVector();
          report('tool_diagnostic', { slug: slug, issue: 'vector_basemap_failed', msg: String((e && e.message) || e).slice(0, 120) });
          setTimeout(function () {
            try { if (!self._glMap) self._glMap = { remove: function () {} }; } catch (e2) {}
            give('vector_ctor', '', true);
          }, 0);
        }
      };
      st.vec = l;

      function watch() {
        var gl = l.getMaplibreMap && l.getMaplibreMap();
        if (!gl) return;
        gl.on('error', function (e) {
          /* До того как стиль встал, ошибка — отказ подложки целиком. После —
             это одна плитка или шрифт: протокол уже попробовал второй адрес,
             и снимать из-за этого карту нельзя. */
          if (!styled) give('style_error', String((e && e.error && (e.error.status || e.error.message)) || '').slice(0, 60));
        });
        gl.once('style.load', function () { styled = true; });
      }
      l.once('add', watch);
      holder.addLayer(l);

      /* Готово = стиль встал И пришла хотя бы одна плитка — с любого адреса.
         Иначе через 15 с ВИДИМОГО времени уходим на растр. Порог щедрый:
         стиль с шрифтами на медленной сети едет секунды, а ложный откат стоит
         человеку перерисовки карты. */
      proto.onTile.push(function () {
        if (settled) return;
        settled = true;
        report('feature_use', { slug: slug, feature: 'basemap_vector', ms: Date.now() - st.t0, via: proto.active ? 'fallback' : 'primary' });
      });
      /* Подложку, снятую с карты (тул переключил на спутник), не судим: без
         карты у неё нет ни холста, ни плиток. Часы пойдут заново, когда её
         вернут. */
      (function arm() {
        later(function () {
          if (settled) return;
          if (!map.hasLayer(holder)) { holder.once('add', arm); return; }
          give(styled ? 'no_tiles' : 'style_timeout', 'ok=' + proto.tileOk + ',fails=' + proto.fails.join('/'));
        }, 15000);
      })();
    }

    function start() {
      if (!webglOk()) return toRaster(glWhy);
      if (vectorRefused()) return toRaster('device_refused');
      var waiting = true;
      var stop = later(function () {
        if (waiting) { waiting = false; toRaster('maplibre_timeout'); }
      }, 15000);
      ensureBridge(L).then(function () {
        if (!waiting) return;
        waiting = false;
        stop();
        mountVector();
      }, function (e) {
        if (!waiting) return;
        waiting = false;
        stop();
        toRaster('maplibre_load', String((e && e.message) || e).slice(0, 60));
      });
    }
    start();

    /* Смена яркости: у вектора — другой стиль (и он же запоминается в опциях
       мостика: снятый и возвращённый слой строит карту заново из опций), у
       растра — фильтр. */
    function onMode(mode) {
      if (st.vec) {
        var u = styleUrl(key());
        try { st.vec.options.style = u; } catch (e) {}
        try { var gl = st.vec.getMaplibreMap && st.vec.getMaplibreMap(); if (gl) gl.setStyle(u, { diff: false }); } catch (e) {}
      }
      if (st.ras) paintRaster(st.ras);
      if (typeof cfg.onStyle === 'function') { try { cfg.onStyle(mode); } catch (e) {} }
    }
    if (follows) {
      listeners.push(onMode);
      if (cfg.control !== false) styleControl(L, map, cfg);
      if (typeof cfg.onStyle === 'function') { try { cfg.onStyle(resolve()); } catch (e) {} }
    }

    return holder;
  }

  /* ── Экспорт: кусок карты в холст ─────────────────────────────────────── */

  var snap = { map: null, el: null, style: '', pr: 0, queue: Promise.resolve() };

  function worldToLngLat(px, py, z) {
    var n = 256 * Math.pow(2, z);
    return [px / n * 360 - 180, Math.atan(Math.sinh(Math.PI * (1 - 2 * py / n))) * 180 / Math.PI];
  }

  /* Без WebGL — те же плитки OpenStreetMap, что у растрового пути карты,
     собранные в холст. Они отдают CORS, так что холст не «портится». */
  function loadImg(url) {
    return new Promise(function (res) {
      var im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = function () { res(im); };
      im.onerror = function () { res(null); };
      im.src = url;
    });
  }
  function rasterSnapshot(o) {
    var W = Math.max(1, Math.round(o.width || 0)), H = Math.max(1, Math.round(o.height || 0));
    var pr = +o.pixelRatio || 1, z = Math.round(o.zoom), sc = Math.pow(2, o.zoom - z), n = Math.pow(2, z);
    var c = document.createElement('canvas');
    c.width = Math.round(W * pr); c.height = Math.round(H * pr);
    var ctx = c.getContext('2d');
    ctx.scale(pr, pr);
    var x0 = Math.floor(o.x / sc / 256), x1 = Math.floor((o.x + W) / sc / 256);
    var y0 = Math.floor(o.y / sc / 256), y1 = Math.floor((o.y + H) / sc / 256);
    var jobs = [], tpl = root.__BASEMAP_RASTER || RASTER;
    for (var tx = x0; tx <= x1; tx++) for (var ty = y0; ty <= y1; ty++) {
      if (ty < 0 || ty >= n) continue;
      jobs.push((function (tx, ty) {
        var wx = ((tx % n) + n) % n;
        return loadImg(tpl.replace('{z}', z).replace('{x}', wx).replace('{y}', ty)).then(function (im) {
          if (im) ctx.drawImage(im, tx * 256 * sc - o.x, ty * 256 * sc - o.y, 256 * sc + 0.5, 256 * sc + 0.5);
          return !!im;
        });
      })(tx, ty));
    }
    return Promise.all(jobs).then(function (r) {
      if (!r.some(Boolean)) return null;
      c.mapSource = 'raster';
      if (o.style === 'dark') {
        try {
          var d = document.createElement('canvas');
          d.width = c.width; d.height = c.height;
          var dx = d.getContext('2d');
          dx.filter = 'invert(1) hue-rotate(180deg) brightness(.9) contrast(.9) saturate(.55)';
          dx.drawImage(c, 0, 0);
          d.mapSource = 'raster';
          return d;
        } catch (e) {}
      }
      return c;
    });
  }

  /**
   * Отрисовать вид карты во внеэкранном MapLibre и вернуть холст.
   * Экспорту нужен ИМЕННО холст: снимок живой карты не годится (другие размер
   * и кадр), а растровых плиток у векторной подложки не существует.
   * Внеэкранная карта одна на страницу, запросы идут по очереди.
   */
  function snapshot(o) {
    var run = snap.queue.then(function () { return doSnapshot(o || {}); });
    snap.queue = run.catch(function () {});
    return run;
  }

  function doSnapshot(o) {
    var W = Math.max(1, Math.round(o.width || 0)), H = Math.max(1, Math.round(o.height || 0));
    var style = STYLES[o.style] ? o.style : 'street';
    if (!webglOk() || vectorRefused()) return rasterSnapshot(o);
    return ensureCore().then(function (ml) {
      var pr = +o.pixelRatio || 1;
      if (!snap.el) {
        snap.el = document.createElement('div');
        snap.el.setAttribute('aria-hidden', 'true');
        snap.el.style.cssText = 'position:fixed;left:-20000px;top:0;pointer-events:none;overflow:hidden';
        document.body.appendChild(snap.el);
      }
      snap.el.style.width = W + 'px';
      snap.el.style.height = H + 'px';
      var center = worldToLngLat(o.x + W / 2, o.y + H / 2, o.zoom);
      var zoom = o.zoom - 1;   // у MapLibre плитка 512 px, у нас шкала 256
      var url = styleUrl(style);
      return new Promise(function (resolveSnap) {
        var done = false, stop = null;
        function finish(ok) {
          if (done) return;
          done = true;
          if (stop) stop();
          if (!ok) { resolveSnap(null); return; }
          try {
            var src = snap.map.getCanvas();
            var c = document.createElement('canvas');
            c.width = Math.round(W * pr); c.height = Math.round(H * pr);
            c.getContext('2d').drawImage(src, 0, 0, c.width, c.height);
            c.mapSource = 'vector';
            resolveSnap(c);
          } catch (e) { resolveSnap(null); }
        }
        try {
          if (!snap.map || snap.pr !== pr) {
            if (snap.map) { try { snap.map.remove(); } catch (e) {} }
            snap.map = new ml.Map({
              container: snap.el, style: url, center: center, zoom: zoom,
              interactive: false, attributionControl: false, fadeDuration: 0,
              pixelRatio: pr, refreshExpiredTiles: false,
              canvasContextAttributes: { preserveDrawingBuffer: true },
            });
            snap.pr = pr;
            snap.style = url;
          } else {
            snap.map.resize();
            if (snap.style !== url) { snap.map.setStyle(url, { diff: false }); snap.style = url; }
            snap.map.jumpTo({ center: center, zoom: zoom });
          }
        } catch (e) {
          done = true;
          refuseVector();
          rasterSnapshot(o).then(resolveSnap, function () { resolveSnap(null); });
          return;
        }
        snap.map.once('idle', function () { finish(true); });
        snap.map.triggerRepaint();
        stop = later(function () {
          // Не всё догрузилось — отдаём, что есть: дырявый кусок лучше пустоты.
          finish(!!(snap.map && snap.map.isStyleLoaded && snap.map.isStyleLoaded()));
        }, +o.timeout || 20000);
      });
    }, function () { return rasterSnapshot(o); });
  }

  /* Плитка 256 px (или 512 при retina) — для экспорта, собранного из плиток
     (видео /gpx-viewer). Рендерится БЛОКАМИ 4×4 с полями: одна внеэкранная
     отрисовка на 16 плиток, а поля не дают подписям обрываться на стыке
     блока. */
  var BLOCK = 4, MARGIN = 64, blocks = {};
  function tileImage(style, z, x, y, o) {
    o = o || {};
    var pr = o.retina ? 2 : 1, n = Math.pow(2, z);
    if (y < 0 || y >= n) return Promise.resolve(null);
    x = ((x % n) + n) % n;
    var bx = Math.floor(x / BLOCK), by = Math.floor(y / BLOCK);
    var k = [style, pr, z, bx, by].join('/');
    if (!blocks[k]) {
      var S = BLOCK * 256;
      blocks[k] = snapshot({
        style: style, zoom: z, pixelRatio: pr,
        x: bx * S - MARGIN, y: by * S - MARGIN, width: S + 2 * MARGIN, height: S + 2 * MARGIN,
      });
    }
    return blocks[k].then(function (c) {
      if (!c) return null;
      var t = document.createElement('canvas');
      t.width = t.height = 256 * pr;
      t.getContext('2d').drawImage(c, (MARGIN + (x - bx * BLOCK) * 256) * pr, (MARGIN + (y - by * BLOCK) * 256) * pr, 256 * pr, 256 * pr, 0, 0, 256 * pr, 256 * pr);
      t.mapSource = c.mapSource;
      return t;
    });
  }

  // Первый штамп — при разборе файла: тул подключает нас ДО своего скрипта,
  // поэтому его панели знают яркость ещё до того, как появится карта.
  injectCss();
  stampHosts(resolve());
  steadyPinch(root.L);   // Leaflet уже на странице; иначе — в base()

  root.__mapTiles = {
    base: base, mode: readMode, resolve: resolve, setMode: setMode,
    snapshot: snapshot, tileImage: tileImage,
    attribution: function (raster) { return raster ? ATTR_RASTER : ATTR_VECTOR; },
    // Подпись для ЭКСПОРТА (текстом в картинке/PDF/видео): по тому, чем
    // нарисован кусок — вектором OpenFreeMap или растром OpenStreetMap.
    creditText: function (source) {
      return source === 'raster' ? '© OpenStreetMap contributors' : 'OpenFreeMap © OpenMapTiles · © OpenStreetMap contributors';
    },
    // Ключей у подложки больше нет. Оставлено, чтобы закэшированная старая
    // страница, приклеивавшая ключ CARTO, не падала на отсутствующей функции.
    withKey: function (u) { return u; },
    // Чистые части правила «карта не уходит за край мира» — для тестов.
    _world: { lat: WORLD_LAT, pad: GL_PAD, minZoomFor: minZoomFor, clampShiftPx: clampShiftPx, keepInWorld: keepInWorld, alignClamped: alignClamped },
  };
}(window));
